import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Car,
  Phone,
  Mail,
  MapPin,
  Clock,
  Star,
  DollarSign,
  Users,
  Zap,
  Shield,
  Calendar,
  Facebook,
  Instagram,
  Twitter,
  Menu,
  X
} from 'lucide-react';
import { whyChooseUs, contactInfo } from '../mock';

// Use relative URL to always match the current domain (works on preview, production, and any custom domain)
const API = '/api';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleType: '',
    pickupDate: '',
    returnDate: '',
    message: ''
  });
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  // Fetch vehicles and reviews on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [vehiclesRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/vehicles`),
          axios.get(`${API}/reviews`)
        ]);
        setVehicles(vehiclesRes.data.vehicles);
        setReviews(reviewsRes.data.reviews);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/bookings`, {
        name: bookingForm.name,
        email: bookingForm.email,
        phone: bookingForm.phone,
        vehicle_type: bookingForm.vehicleType,
        pickup_date: bookingForm.pickupDate,
        return_date: bookingForm.returnDate,
        message: bookingForm.message
      });
      
      toast.success(response.data.message);
      setBookingDialogOpen(false);
      setBookingForm({
        name: '',
        email: '',
        phone: '',
        vehicleType: '',
        pickupDate: '',
        returnDate: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Failed to submit booking. Please try again.');
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      toast.success(response.data.message);
      setContactForm({
        name: '',
        email: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast.error('Failed to send message. Please try again.');
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      DollarSign,
      Car,
      Clock,
      Users,
      Zap,
      Shield
    };
    const IconComponent = icons[iconName];
    return IconComponent ? <IconComponent className="w-8 h-8" /> : null;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_island-rentals-8/artifacts/hwmy1m19_MBCARRENTALSLOG2.png" 
                alt="MB Car Rentals Logo" 
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">MB Car Rentals</h1>
                <p className="text-xs text-gray-600">Efficient • Affordable • Reliable</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-sky-600 font-medium transition-colors">
                Home
              </button>
              <button onClick={() => scrollToSection('vehicles')} className="text-gray-700 hover:text-sky-600 font-medium transition-colors">
                Vehicles
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-gray-700 hover:text-sky-600 font-medium transition-colors">
                Pricing
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-sky-600 font-medium transition-colors">
                About Us
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-sky-600 font-medium transition-colors">
                Contact
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-3">
                <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-sky-600 font-medium py-2 text-left transition-colors">
                  Home
                </button>
                <button onClick={() => scrollToSection('vehicles')} className="text-gray-700 hover:text-sky-600 font-medium py-2 text-left transition-colors">
                  Vehicles
                </button>
                <button onClick={() => scrollToSection('pricing')} className="text-gray-700 hover:text-sky-600 font-medium py-2 text-left transition-colors">
                  Pricing
                </button>
                <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-sky-600 font-medium py-2 text-left transition-colors">
                  About Us
                </button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-sky-600 font-medium py-2 text-left transition-colors">
                  Contact
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-20 min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-blue-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-sky-100 text-sky-700 rounded-full text-sm font-medium">
                🌴 Your Trusted Car Rental in The Bahamas
              </div>
              <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Drive The Bahamas
                <span className="block text-sky-600">Your Way</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Experience freedom and flexibility with our affordable, reliable car rentals. 
                Explore paradise at your own pace with MB Car Rentals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="lg" className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                      Book Now
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-2xl">Book Your Vehicle</DialogTitle>
                      <DialogDescription>
                        Fill out the form below and we'll get back to you shortly to confirm your reservation.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleBookingSubmit} className="space-y-4 mt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            value={bookingForm.name}
                            onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                            required
                            placeholder="John Doe"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={bookingForm.email}
                            onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                            required
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={bookingForm.phone}
                          onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                          required
                          placeholder="+1 (242) 555-0123"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vehicleType">Vehicle Type *</Label>
                        <Select
                          value={bookingForm.vehicleType}
                          onValueChange={(value) => setBookingForm({ ...bookingForm, vehicleType: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a vehicle type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="suv">SUV</SelectItem>
                            <SelectItem value="sedan">Sedan</SelectItem>
                            <SelectItem value="compact">Compact</SelectItem>
                            <SelectItem value="convertible">Convertible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="pickupDate">Pickup Date *</Label>
                          <Input
                            id="pickupDate"
                            type="date"
                            value={bookingForm.pickupDate}
                            onChange={(e) => setBookingForm({ ...bookingForm, pickupDate: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="returnDate">Return Date *</Label>
                          <Input
                            id="returnDate"
                            type="date"
                            value={bookingForm.returnDate}
                            onChange={(e) => setBookingForm({ ...bookingForm, returnDate: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Additional Information</Label>
                        <Textarea
                          id="message"
                          value={bookingForm.message}
                          onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                          placeholder="Any special requests or questions?"
                          rows={3}
                        />
                      </div>
                      <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700">
                        Submit Booking Request
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-gray-300 hover:border-sky-600 px-8 py-6 text-lg transition-colors"
                  onClick={() => scrollToSection('vehicles')}
                >
                  View Vehicles
                </Button>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div>
                  <div className="text-3xl font-bold text-gray-900">500+</div>
                  <div className="text-sm text-gray-600">Happy Customers</div>
                </div>
                <div className="w-px h-12 bg-gray-300"></div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">50+</div>
                  <div className="text-sm text-gray-600">Quality Vehicles</div>
                </div>
                <div className="w-px h-12 bg-gray-300"></div>
                <div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <div className="text-sm text-gray-600">5-Star Rated</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1200&q=80"
                  alt="Luxury car in The Bahamas"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-sky-100 p-3 rounded-full">
                    <Calendar className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Starting from</div>
                    <div className="text-2xl font-bold text-gray-900">$75/day</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Vehicles Section */}
      <section id="vehicles" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Vehicles</h2>
            <p className="text-xl text-gray-600">Choose from our wide selection of quality vehicles</p>
          </div>
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-sky-600 border-r-transparent"></div>
              <p className="mt-4 text-gray-600">Loading vehicles...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
              {vehicles.map((vehicle) => (
              <Card key={vehicle.id} className="group hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                <div className="relative overflow-hidden">
                  <img
                    src={vehicle.image}
                    alt={vehicle.name}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4 bg-sky-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    ${vehicle.price}/day
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{vehicle.name}</CardTitle>
                  <CardDescription>{vehicle.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {vehicle.features.map((feature, index) => (
                        <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          {feature}
                        </span>
                      ))}
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="w-full bg-sky-600 hover:bg-sky-700">
                          Book This Vehicle
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-2xl">Book {vehicle.name}</DialogTitle>
                          <DialogDescription>
                            Complete the form to reserve this vehicle at ${vehicle.price}/day
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleBookingSubmit} className="space-y-4 mt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="name">Full Name *</Label>
                              <Input
                                id="name"
                                value={bookingForm.name}
                                onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                                required
                                placeholder="John Doe"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="email">Email *</Label>
                              <Input
                                id="email"
                                type="email"
                                value={bookingForm.email}
                                onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                                required
                                placeholder="john@example.com"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone Number *</Label>
                            <Input
                              id="phone"
                              type="tel"
                              value={bookingForm.phone}
                              onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                              required
                              placeholder="+1 (242) 555-0123"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="pickupDate">Pickup Date *</Label>
                              <Input
                                id="pickupDate"
                                type="date"
                                value={bookingForm.pickupDate}
                                onChange={(e) => setBookingForm({ ...bookingForm, pickupDate: e.target.value })}
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="returnDate">Return Date *</Label>
                              <Input
                                id="returnDate"
                                type="date"
                                value={bookingForm.returnDate}
                                onChange={(e) => setBookingForm({ ...bookingForm, returnDate: e.target.value })}
                                required
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="message">Additional Information</Label>
                            <Textarea
                              id="message"
                              value={bookingForm.message}
                              onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                              placeholder="Any special requests or questions?"
                              rows={3}
                            />
                          </div>
                          <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700">
                            Submit Booking Request
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Note */}
      <section id="pricing" className="py-16 bg-gradient-to-r from-sky-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Transparent Pricing</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              All our rates include comprehensive insurance, unlimited mileage, and 24/7 roadside assistance. 
              No hidden fees, no surprises.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <DollarSign className="w-12 h-12 text-sky-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Daily Rates</h3>
              <p className="text-gray-600">Starting from $75/day</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <Calendar className="w-12 h-12 text-sky-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Special Rates</h3>
              <p className="text-gray-600">Weekly & Monthly rates available</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <Shield className="w-12 h-12 text-sky-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Deposit</h3>
              <p className="text-gray-600">$150 refundable deposit</p>
            </div>
          </div>

          {/* Rental Requirements */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Rental Requirements</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Users className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Driver Age Requirement</h4>
                    <p className="text-gray-600 text-sm">Drivers must be 25 years or older with a valid driver's license</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Calendar className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Minimum Rental Period</h4>
                    <p className="text-gray-600 text-sm">Two day minimum rental required for all vehicles</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <DollarSign className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Refundable Deposit</h4>
                    <p className="text-gray-600 text-sm">$150 deposit required, fully refundable upon return</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Shield className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Valid Identification</h4>
                    <p className="text-gray-600 text-sm">Valid government-issued ID or passport required</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose MB Car Rentals?</h2>
            <p className="text-xl text-gray-600">Your satisfaction is our top priority</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mb-4 text-sky-600">
                    {getIcon(item.icon)}
                  </div>
                  <CardTitle className="text-xl">{item.title}</CardTitle>
                  <CardDescription className="text-base">{item.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-20 bg-gradient-to-br from-sky-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-xl text-gray-600">Real experiences from real travelers</p>
          </div>
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-sky-600 border-r-transparent"></div>
              <p className="mt-4 text-gray-600">Loading reviews...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {reviews.map((review) => (
                <Card key={review.id} className="hover:shadow-xl transition-shadow duration-300">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-lg">{review.name}</CardTitle>
                      <div className="flex">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </div>
                    <CardDescription className="text-sm text-gray-500">
                      {review.location} • {new Date(review.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 italic">"{review.comment}"</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600">We're here to help with any questions</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Phone className="w-6 h-6 text-sky-600" />
                    Phone
                  </CardTitle>
                  <CardDescription className="text-lg">{contactInfo.phone}</CardDescription>
                  <CardDescription className="text-lg">{contactInfo.phone2}</CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Mail className="w-6 h-6 text-sky-600" />
                    Email
                  </CardTitle>
                  <CardDescription className="text-lg">{contactInfo.email}</CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <MapPin className="w-6 h-6 text-sky-600" />
                    Location
                  </CardTitle>
                  <CardDescription className="text-lg">{contactInfo.address}</CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Clock className="w-6 h-6 text-sky-600" />
                    Hours
                  </CardTitle>
                  <CardDescription className="text-lg">{contactInfo.hours}</CardDescription>
                </CardHeader>
              </Card>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                <CardDescription>Fill out the form and we'll get back to you within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="contactName">Name *</Label>
                    <Input
                      id="contactName"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                      placeholder="Your name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactEmail">Email *</Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactMessage">Message *</Label>
                    <Textarea
                      id="contactMessage"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      placeholder="How can we help you?"
                      rows={5}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700">
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_island-rentals-8/artifacts/hwmy1m19_MBCARRENTALSLOG2.png" 
                  alt="MB Car Rentals Logo" 
                  className="h-10 w-auto"
                />
                <span className="text-xl font-bold">MB Car Rentals</span>
              </div>
              <p className="text-gray-400 text-sm">
                Your trusted car rental partner in The Bahamas. Efficient, affordable, and reliable service since 2020.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <button onClick={() => scrollToSection('home')} className="hover:text-sky-400 transition-colors">
                    Home
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('vehicles')} className="hover:text-sky-400 transition-colors">
                    Vehicles
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('pricing')} className="hover:text-sky-400 transition-colors">
                    Pricing
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('about')} className="hover:text-sky-400 transition-colors">
                    About Us
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Contact Info</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {contactInfo.phone}
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {contactInfo.phone2}
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {contactInfo.email}
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Eleuthera, Palmetto Point
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Follow Us</h3>
              <div className="flex gap-4">
                <a
                  href={contactInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href={contactInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href={contactInfo.social.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} MB Car Rentals. All rights reserved. | Efficient • Affordable • Reliable</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;